/**
 * Guest appearance (docs/STORY.md §5). A look is chosen in the browser, stored only there,
 * and sent to the room as one small integer that the server validates.
 *
 * Rules from the §10 reviewer: no gender field, no shaved or bald heads, no monastic robes,
 * no hats, no rare or paid looks. Every combination is valid and equal.
 */
export const AGES = ['child', 'young', 'middle', 'elder'] as const;
export const BUILDS = ['slim', 'mid', 'full'] as const;
export const SKIN_TONE_COUNT = 6;
export const HAIR_STYLES = ['short', 'fringe', 'high', 'tail', 'long', 'low'] as const;
export const HAIR_COLORS = ['black', 'darkbrown', 'brown', 'salt', 'silver'] as const;
export const OUTFITS = ['trang', 'baba', 'somi', 'len', 'aodai'] as const;
export const LOOK_CHANGE_MS = 1500;

export type Age = typeof AGES[number];
export type Build = typeof BUILDS[number];
export type HairStyle = typeof HAIR_STYLES[number];
export type HairColor = typeof HAIR_COLORS[number];
export type Outfit = typeof OUTFITS[number];
export type Look = {
  age: Age; build: Build; skin: number; hair: HairStyle; hairColor: HairColor; outfit: Outfit;
  glasses: boolean; scarf: boolean;
  /** Only an elder carries a cane, and only while walking. */
  cane: boolean;
};

/** Codes below this are the pre-v1 random robe numbers (0–3) and are mapped, never rejected. */
export const APPEARANCE_BASE = 1000;
const RADIX = [AGES.length, BUILDS.length, SKIN_TONE_COUNT, HAIR_STYLES.length, HAIR_COLORS.length, OUTFITS.length, 8] as const;
export const APPEARANCE_COUNT = RADIX.reduce((product, size) => product * size, 1);

export function normalizeLook(look: Look): Look {
  return { ...look, skin: Math.min(SKIN_TONE_COUNT - 1, Math.max(0, Math.trunc(look.skin))), cane: look.cane && look.age === 'elder' };
}

export function encodeLook(input: Look): number {
  const look = normalizeLook(input);
  const digits = [AGES.indexOf(look.age), BUILDS.indexOf(look.build), look.skin, HAIR_STYLES.indexOf(look.hair), HAIR_COLORS.indexOf(look.hairColor), OUTFITS.indexOf(look.outfit), (look.glasses ? 1 : 0) | (look.scarf ? 2 : 0) | (look.cane ? 4 : 0)];
  if (digits.some(digit => digit < 0)) throw new Error('invalid_look');
  let code = 0;
  for (let i = 0; i < RADIX.length; i++) code = code * RADIX[i] + digits[i];
  return APPEARANCE_BASE + code;
}

export function isAppearanceCode(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= APPEARANCE_BASE && value < APPEARANCE_BASE + APPEARANCE_COUNT;
}

/** The canonical code for a valid one (a cane bit on a non-elder is dropped), or null. */
export function canonicalAppearance(value: unknown): number | null {
  return isAppearanceCode(value) ? encodeLook(decodeAppearance(value)) : null;
}

/** The four robes guests had before v1, kept so old reservations still render. */
const LEGACY: Outfit[] = ['len', 'baba', 'trang', 'somi'];

export function decodeAppearance(code: number): Look {
  if (!Number.isInteger(code) || code < APPEARANCE_BASE || code >= APPEARANCE_BASE + APPEARANCE_COUNT) {
    const index = Number.isInteger(code) && code >= 0 ? code % LEGACY.length : 0;
    return { age: 'young', build: 'mid', skin: 1, hair: 'short', hairColor: index === 1 ? 'darkbrown' : 'black', outfit: LEGACY[index], glasses: false, scarf: false, cane: false };
  }
  let rest = code - APPEARANCE_BASE;
  const digits: number[] = [];
  for (let i = RADIX.length - 1; i >= 0; i--) { digits[i] = rest % RADIX[i]; rest = Math.floor(rest / RADIX[i]); }
  const extras = digits[6];
  return normalizeLook({
    age: AGES[digits[0]], build: BUILDS[digits[1]], skin: digits[2], hair: HAIR_STYLES[digits[3]], hairColor: HAIR_COLORS[digits[4]], outfit: OUTFITS[digits[5]],
    glasses: (extras & 1) === 1, scarf: (extras & 2) === 2, cane: (extras & 4) === 4,
  });
}

/** A default look: every tone, hair and outfit equally likely; adults only (a child is chosen, never assigned). */
export function randomLook(random: () => number = Math.random): Look {
  const pick = <T,>(items: readonly T[]) => items[Math.min(items.length - 1, Math.floor(random() * items.length))];
  const age = pick(['young', 'middle', 'elder'] as const);
  return normalizeLook({
    age, build: pick(BUILDS), skin: Math.min(SKIN_TONE_COUNT - 1, Math.floor(random() * SKIN_TONE_COUNT)), hair: pick(HAIR_STYLES), hairColor: pick(HAIR_COLORS), outfit: pick(OUTFITS),
    glasses: random() < 0.2, scarf: random() < 0.15, cane: age === 'elder' && random() < 0.4,
  });
}
